/* cs-buybox.js — pack selection, per-unit variant rows, add-ons, one total.
 *
 * Works anywhere the block renders, not just the product page. The catalogue's
 * "Seçenekler" button opens a quick-add modal that injects the same block long
 * after this file has run, so there is no single element to bind to at load:
 * every listener is delegated from the document and each block instance is
 * resolved from the event.
 *
 * All state lives in the DOM — the checked radio, and data-checked on the
 * add-on cards — so two instances on one page (product page plus modal) cannot
 * fight over a shared variable.
 *
 * One compute, one render. The header price, the badge, the pack subtitles, the
 * add-on cards and the button label all come from the same state, so the button
 * cannot say one number while the header says another.
 *
 * Pack totals are the sum of the variant prices the buyer actually picked,
 * minus the tier discount the cart will apply. That is what lets a colour on
 * offer show up in the pack price rather than only at the checkout. The schema
 * figures stay as the server-rendered fallback for the moment before this file
 * runs, and for the case where the config carries no variant prices.
 */
(function () {
  'use strict';

  var HOST = '[data-cs-packs]';

  /* -------------------------------------------------------------- instance -- */

  // Everything a render needs, derived from one block element. The scope is the
  // product-info wrapper it sits in, which is what keeps the modal's price row
  // and button separate from the ones on the page behind it.
  function instanceFor(host) {
    if (!host) return null;
    var raw = host.querySelector('[data-cs-packs-config]');
    if (!raw) return null;
    var config;
    try {
      config = JSON.parse(raw.textContent);
    } catch (e) {
      return null;
    }
    return {
      host: host,
      scope: host.closest('product-info') || host.closest('.product__info-wrapper') || document,
      config: config,
      copy: config.copy || {},
      swatches: config.swatches || {},
      rows: host.querySelector('[data-cs-rows]'),
    };
  }

  function instanceFromEvent(target) {
    if (!target || !target.closest) return null;
    return instanceFor(target.closest(HOST));
  }

  /* ---------------------------------------------------------------- money -- */

  function money(minorUnits) {
    return Math.round(minorUnits / 100).toLocaleString('tr-TR');
  }

  function fill(template, values) {
    var out = String(template || '');
    Object.keys(values).forEach(function (key) {
      out = out.split('[' + key + ']').join(values[key]);
    });
    return out;
  }

  /* -------------------------------------------------------------- pricing -- */

  function variantFor(inst, colour, size) {
    return (inst.config.variantOptions || []).filter(function (v) {
      return v.option1 === colour && v.option2 === size;
    })[0] || null;
  }

  function priceFor(inst, colour, size) {
    var match = variantFor(inst, colour, size);
    return match && typeof match.price === 'number' ? match.price : 0;
  }

  // The dearest colour is the reference. Anything under it is on offer, and the
  // gap is the figure the buyer is shown — no second list of discounted colours
  // to keep in step with the prices in Shopify.
  function basePrice(inst) {
    var prices = (inst.config.variantOptions || []).map(function (v) {
      return typeof v.price === 'number' ? v.price : 0;
    });
    return prices.length ? Math.max.apply(null, prices) : 0;
  }

  function colourSaving(inst, colour) {
    var prices = (inst.config.variantOptions || []).filter(function (v) {
      return v.option1 === colour && typeof v.price === 'number';
    }).map(function (v) { return v.price; });
    if (!prices.length) return 0;
    return Math.max(0, basePrice(inst) - Math.min.apply(null, prices));
  }

  function rowSelections(inst) {
    return [].slice.call(inst.rows ? inst.rows.querySelectorAll('[data-cs-row]') : []).map(function (row) {
      var colour = row.querySelector('[data-cs-colour]');
      var size = row.querySelector('[data-cs-size]');
      return { colour: colour ? colour.value : '', size: size ? size.value : '' };
    });
  }

  /* ---------------------------------------------------------------- state -- */

  function packs(inst) {
    return [].slice.call(inst.host.querySelectorAll('[data-cs-pack]'));
  }

  function addons(inst) {
    return [].slice.call(inst.host.querySelectorAll('[data-cs-addon]'));
  }

  function selectedPack(inst) {
    var all = packs(inst);
    return all.filter(function (pack) {
      return pack.querySelector('.cs-pack__input').checked;
    })[0] || all[0];
  }

  function packState(pack, inst) {
    var compare = parseInt(pack.dataset.compare, 10) || 0;
    var quantity = parseInt(pack.dataset.qty, 10) || 1;
    var discount = parseInt(pack.dataset.discount, 10) || 0;
    var total = parseInt(pack.dataset.total, 10) || 0;
    var unit = parseInt(pack.dataset.unit, 10) || 0;
    var colourSaved = 0;

    // Every row is priced or none of them is: a half-computed total would be
    // worse than the schema figure it replaces.
    var rows = inst ? rowSelections(inst).slice(0, quantity) : [];
    var priced = rows.length === quantity && rows.every(function (r) {
      return priceFor(inst, r.colour, r.size) > 0;
    });

    if (priced) {
      var sum = 0;
      rows.forEach(function (r) {
        sum += priceFor(inst, r.colour, r.size);
        colourSaved += colourSaving(inst, r.colour);
      });
      total = sum - discount;
      unit = Math.round(total / quantity);
    }

    return {
      node: pack,
      quantity: quantity,
      total: total,
      unit: unit,
      compare: compare,
      saved: compare - total,
      colourSaved: colourSaved,
      percent: compare ? Math.round((1 - total / compare) * 100) : 0,
    };
  }

  function isFree(addon, quantity) {
    var freeFrom = parseInt(addon.dataset.freeFrom, 10) || 0;
    return freeFrom > 0 && quantity >= freeFrom;
  }

  function isTicked(addon) {
    return addon.getAttribute('data-checked') === 'true';
  }

  /* --------------------------------------------------------------- render -- */

  function renderPacks(inst, state) {
    packs(inst).forEach(function (pack) {
      var selected = pack === state.node;
      pack.classList.toggle('cs-pack--selected', selected);
      pack.querySelector('.cs-pack__input').checked = selected;

      var each = packState(pack, inst);
      var sub = pack.querySelector('.cs-pack__sub');
      if (sub) {
        if (sub.dataset.template === undefined) sub.dataset.template = sub.textContent;
        // at one unit there is no bulk saving, so the line carries no figure —
        // the compare-at price is already struck through beside the header
        var savingPhrase = each.quantity > 1 && each.saved > 0
          ? '<span class="cs-pack__saved">' + fill(inst.copy.savingLine, { tutar: money(each.saved) }) + '</span>'
          : '';
        var text = fill(sub.dataset.template, { kazanc: savingPhrase });
        if (sub.innerHTML !== text) sub.innerHTML = text;
        sub.hidden = text.trim() === '';
      }

      var totalNode = pack.querySelector('.cs-pack__total');
      totalNode.textContent = money(each.total) + ' TL';
      // red is reserved for money coming off the price, so it marks the colour
      // offer as well as the saving figure on the line below
      totalNode.classList.toggle('cs-pack__total--sale', each.colourSaved > 0);
      pack.querySelector('.cs-pack__compare').textContent = money(each.compare) + ' TL';
    });

    // the rows element is moved rather than rebuilt, so the selects keep their
    // values when the buyer switches packs and switches back
    var slot = state.node.querySelector('[data-cs-rows-slot]');
    if (slot && inst.rows && inst.rows.parentNode !== slot) slot.appendChild(inst.rows);
    if (inst.rows) inst.rows.hidden = false;

    [].slice.call(inst.rows ? inst.rows.querySelectorAll('[data-cs-row]') : []).forEach(function (row, index) {
      row.hidden = index >= state.quantity;
      row.querySelector('.cs-row__label').hidden = state.quantity < 2;
    });
  }

  function renderHeaderPrice(inst, state) {
    // every price node is written: querySelector with a comma list returns
    // whichever comes first in the DOM, and the hidden .price__regular precedes
    // .price__sale — which is how the header once kept a stale figure
    var now = inst.scope.querySelectorAll(
      '.product-page-price .price-item--sale, .product-page-price .price__regular .price-item--regular'
    );
    for (var n = 0; n < now.length; n++) now[n].textContent = money(state.total) + ' TL';

    var was = inst.scope.querySelectorAll('.product-page-price .price__compare-price .price-item--regular');
    for (var w = 0; w < was.length; w++) was[w].textContent = money(state.compare) + ' TL';

    var badge = inst.scope.querySelector('.price__badge-sale');
    if (badge) {
      badge.textContent = fill(inst.copy.discountBadge, { yuzde: state.percent });
      badge.hidden = state.percent <= 0;
    }
  }

  function renderAddons(inst, state) {
    addons(inst).forEach(function (addon) {
      var price = parseInt(addon.dataset.price, 10) || 0;
      var free = isFree(addon, state.quantity);

      // one that comes with the pack is not a control the buyer can switch off,
      // and one that still costs money is never pre-ticked
      if (free) addon.setAttribute('data-checked', 'false');
      var ticked = !free && isTicked(addon);

      addon.classList.toggle('cs-addon--free', free);
      addon.classList.toggle('cs-addon--checked', ticked);
      addon.setAttribute('aria-checked', free || ticked ? 'true' : 'false');
      addon.setAttribute('aria-disabled', free ? 'true' : 'false');
      addon.tabIndex = free ? -1 : 0;

      var amount = addon.querySelector('[data-cs-addon-amount]');
      amount.hidden = free;
      amount.textContent = money(price) + ' TL';
      addon.querySelector('[data-cs-addon-free]').hidden = !free;

      var was = addon.querySelector('[data-cs-addon-was]');
      was.hidden = !free;
      was.textContent = money(price) + ' TL';

      var tag = addon.querySelector('[data-cs-addon-tag]');
      var freeFrom = parseInt(addon.dataset.freeFrom, 10) || 0;
      var label = free ? fill(inst.copy.includedTag, { adet: freeFrom }) : addon.dataset.tagOptional || '';
      tag.textContent = label;
      tag.hidden = label === '';
      tag.classList.toggle('cs-chip--terra', !free && label !== '');
      tag.classList.toggle('cs-chip--sage', free);
    });
  }

  function renderButton(inst, state) {
    var total = state.total;
    addons(inst).forEach(function (addon) {
      if (!isFree(addon, state.quantity) && isTicked(addon)) {
        total += parseInt(addon.dataset.price, 10) || 0;
      }
    });

    var label = fill(inst.copy.addToCart, { tutar: money(total) });
    var buttons = inst.scope.querySelectorAll(
      '.product-form__buttons .atc-button, .sticky-atc__add_button, .quick-add__submit'
    );
    for (var i = 0; i < buttons.length; i++) {
      var slot = buttons[i].querySelector('.cs-atc-label');
      if (!slot) {
        buttons[i].textContent = '';
        slot = document.createElement('span');
        slot.className = 'cs-atc-label';
        buttons[i].appendChild(slot);
      }
      if (slot.textContent !== label) slot.textContent = label;
    }
  }

  function renderSwatches(inst) {
    if (!inst.rows) return;
    [].slice.call(inst.rows.querySelectorAll('[data-cs-colour]')).forEach(function (select) {
      var field = select.closest('[data-cs-colour-field]');
      var dot = field && field.querySelector('[data-cs-dot]');
      if (!dot) return;
      var hex = inst.swatches[(select.value || '').trim()];
      dot.style.backgroundColor = hex || 'transparent';
      dot.hidden = !hex;

      // Named in the list, so the offer is visible before the buyer commits —
      // but never on the selected option. A closed native select shows the
      // selected option's own text, which would put the figure on the field
      // twice, once as text and once as the tag beside it.
      [].slice.call(select.options).forEach(function (option) {
        var saving = option.selected ? 0 : colourSaving(inst, option.value);
        var label = saving > 0
          ? fill(inst.copy.colourSaleOption, { renk: option.value, tutar: money(saving) })
          : option.value;
        if (option.textContent !== label) option.textContent = label;
      });

      // and on the closed field, where the list is not
      var tag = field.querySelector('[data-cs-colour-tag]');
      if (tag) {
        var selected = colourSaving(inst, select.value);
        var text = selected > 0 ? fill(inst.copy.colourSaleTag, { tutar: money(selected) }) : '';
        if (tag.textContent !== text) tag.textContent = text;
        tag.hidden = selected <= 0;
      }
    });
  }

  function render(inst) {
    if (!inst) return;
    var pack = selectedPack(inst);
    if (!pack) return;
    var state = packState(pack, inst);
    renderPacks(inst, state);
    renderHeaderPrice(inst, state);
    renderAddons(inst, state);
    renderSwatches(inst);
    renderButton(inst, state);
  }

  function renderAll() {
    [].slice.call(document.querySelectorAll(HOST)).forEach(function (host) {
      var inst = instanceFor(host);
      if (!inst) return;
      if (host.dataset.csReady !== '1') {
        addons(inst).forEach(function (addon) { addon.setAttribute('data-checked', 'false'); });
        host.dataset.csReady = '1';
      }
      render(inst);
    });
  }

  /* --------------------------------------------------------------- events -- */

  function onClick(event) {
    // a click on a dropdown must not also re-pick the card it sits in
    if (event.target.closest('select')) return;

    var inst = instanceFromEvent(event.target);
    if (!inst) return;

    if (event.target.closest('[data-cs-size-guide]')) {
      event.preventDefault();
      openSizeGuide(inst);
      return;
    }

    var pack = event.target.closest('[data-cs-pack]');
    if (pack) {
      pack.querySelector('.cs-pack__input').checked = true;
      render(inst);
      return;
    }

    var addon = event.target.closest('[data-cs-addon]');
    if (addon) {
      if (addon.getAttribute('aria-disabled') === 'true') return;
      addon.setAttribute('data-checked', isTicked(addon) ? 'false' : 'true');
      render(inst);
    }
  }

  function onKeydown(event) {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    var inst = instanceFromEvent(event.target);
    if (!inst) return;
    var addon = event.target.closest('[data-cs-addon]');
    if (!addon || addon.getAttribute('aria-disabled') === 'true') return;
    event.preventDefault();
    addon.setAttribute('data-checked', isTicked(addon) ? 'false' : 'true');
    render(inst);
  }

  function onChange(event) {
    if (!event.target.matches || !event.target.matches('[data-cs-colour], [data-cs-size]')) return;
    render(instanceFromEvent(event.target));
  }

  function openSizeGuide(inst) {
    var alt = (inst.copy.sizeGuideAlt || '').trim().toLocaleLowerCase('tr-TR');
    var slides = [].slice.call(document.querySelectorAll('.product__media-item'));
    var target = slides.filter(function (slide) {
      var img = slide.querySelector('img');
      return img && (img.getAttribute('alt') || '').trim().toLocaleLowerCase('tr-TR') === alt;
    })[0] || slides[slides.length - 1];
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    var opener = target.querySelector('.product__modal-opener, modal-opener button, [data-modal]');
    if (opener) opener.click();
  }

  /* ---------------------------------------------------------- add to cart -- */

  function variantIdFor(inst, colour, size) {
    var match = (inst.config.variantOptions || []).filter(function (v) {
      return v.option1 === colour && v.option2 === size;
    })[0];
    return match ? match.id : 0;
  }

  function drawerSections() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.getSectionsToRender !== 'function') return null;
    try {
      return { drawer: drawer, ids: drawer.getSectionsToRender().map(function (s) { return s.section || s.id; }) };
    } catch (e) {
      return null;
    }
  }

  var submitting = false;

  async function addToCart(inst) {
    var state = packState(selectedPack(inst), inst);
    var bundleId = 'b_' + Date.now();
    var packLabel = state.quantity + ' adet';
    var items = [];

    [].slice.call(inst.rows ? inst.rows.querySelectorAll('[data-cs-row]') : []).forEach(function (row, index) {
      if (index >= state.quantity) return;
      var id = variantIdFor(
        inst,
        row.querySelector('[data-cs-colour]').value,
        row.querySelector('[data-cs-size]').value
      );
      if (!id) return;
      var properties = { _bundle_id: bundleId };
      if (state.quantity > 1) properties[inst.copy.packageProperty || 'Paket'] = packLabel;
      items.push({ id: id, quantity: 1, properties: properties });
    });

    if (!items.length) throw new Error('no variant resolved for the selected rows');

    var inCart = {};
    try {
      var current = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      (current.items || []).forEach(function (line) { inCart[line.id] = true; });
    } catch (e) { /* Shopify merges a duplicate line */ }

    // A free add-on still has to be a real line: the BXGY discount is what
    // zeroes it, so leaving it out would promise something checkout never gives.
    addons(inst).forEach(function (addon) {
      var free = isFree(addon, state.quantity);
      if (!free && !isTicked(addon)) return;
      var id = parseInt(addon.dataset.variant, 10);
      if (!id || inCart[id]) return;
      items.push({
        id: id,
        quantity: 1,
        properties: { _bundle_id: bundleId, _addon: free ? 'auto' : 'manual' },
      });
    });

    var body = { items: items };
    var sections = drawerSections();
    if (sections) {
      body.sections = sections.ids;
      body.sections_url = window.location.pathname;
    }

    var res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body),
    });
    var payload = await res.json();
    if (!res.ok) throw new Error(payload.description || 'cart/add failed');

    if (sections && payload.sections) {
      sections.drawer.renderContents(payload);
      // Shrine keeps the empty-cart class on the drawer element as well as the
      // inner wrapper, and renderContents only clears the inner one
      sections.drawer.classList.remove('is-empty');
      var inner = sections.drawer.querySelector('.drawer__inner');
      if (inner) inner.classList.remove('is-empty');
      if (typeof sections.drawer.open === 'function') sections.drawer.open();
    } else {
      window.location.href = '/cart';
    }
  }

  function onAddToCart(event) {
    var button = event.target.closest
      ? event.target.closest('.atc-button, .sticky-atc__add_button, .quick-add__submit')
      : null;
    if (!button || submitting) return;
    if (button.dataset.csFallback === '1') return;

    // The catalogue card's own button carries the same class but has no block
    // beside it — that one must keep opening the modal, so it is left alone.
    var scope = button.closest('product-info') || button.closest('.product__info-wrapper');
    var inst = scope ? instanceFor(scope.querySelector(HOST)) : null;
    if (!inst) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    submitting = true;
    button.classList.add('loading');
    addToCart(inst)
      .catch(function (error) {
        if (window.console) console.warn('[cs-buybox] falling back to the theme add to cart:', error);
        button.dataset.csFallback = '1';
        button.click();
        delete button.dataset.csFallback;
      })
      .then(function () {
        submitting = false;
        button.classList.remove('loading');
      });
  }

  /* ----------------------------------------------------------------- init -- */

  var scheduled = false;
  var observer = new MutationObserver(function () {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      renderAll();
    });
  });

  function start() {
    renderAll();
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('change', onChange);
    document.addEventListener('click', onAddToCart, true);
    // the quick-add modal injects a whole block long after this file has run
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
